import { AuthApi } from '@/apis/auth';
import { DeviceApi } from '@/apis/device';
import { EApiBaseCode } from '@/constants';
import { hydrateUserProfileFromApi } from '@/providers/auth/hydrate-user-profile';
import { useUserInfoStore } from '@/store/user/slice';
import {
    reportGuestSessionAttempt,
    reportGuestSessionFail,
    reportGuestSessionFailFromError,
    reportGuestSessionSuccess,
} from '@/utils/arms/guest-session-events';
import { getOrCreateDeviceUuid, getOrCreateInstallSeq } from '@/utils/device/device-credentials';
import { persistTouristDeviceId } from '@/utils/device/server-device-id';
import { registerSessionBootstrapRunner } from '@/utils/fetch/session-gate';
import { setSessionReadyCookie } from '@/utils/fetch/session-ready-cookie';
import { syncArmsConfig } from '@/utils/sync-arms-config';
import { registerTrackerParams } from '@/utils/tracker';

let establishSessionPromise: Promise<void> | null = null;
let bootstrapPromise: Promise<void> | null = null;

/** 返回 establishSession 的 in-flight promise（供 useLandingData 等待 device_id 就绪） */
export function getEstablishSessionInflight(): Promise<void> | null {
    return establishSessionPromise;
}

/**
 * 注册设备 + 游客 postLogin + 写 session_ready cookie。
 * waitForSession 只应等待本链，不应等待 profile hydrate。
 */
export function establishSession(): Promise<void> {
    if (establishSessionPromise) {
        return establishSessionPromise;
    }
    establishSessionPromise = (async () => {
        reportGuestSessionAttempt();
        let guestSessionFailReported = false;

        try {
            const [device_uuid, install_seq] = await Promise.all([getOrCreateDeviceUuid(), getOrCreateInstallSeq()]);

            const registerRes = await DeviceApi.postRegisterDevice({ device_uuid, install_seq });
            if (registerRes.code !== EApiBaseCode.SUCCESS || !registerRes.data?.device_id) {
                guestSessionFailReported = true;
                reportGuestSessionFail({
                    fail_reason: 'device_register_failed',
                    fail_detail: `code:${registerRes.code};${registerRes.message ?? ''}`,
                });
                throw new Error(registerRes.message || 'device register failed');
            }

            persistTouristDeviceId(registerRes.data.device_id);

            const loginRes = await AuthApi.postLogin({
                device_id: registerRes.data.device_id,
            });
            if (loginRes.code !== EApiBaseCode.SUCCESS || !loginRes.data?.user_id) {
                guestSessionFailReported = true;
                reportGuestSessionFail({
                    fail_reason: 'guest_post_login_failed',
                    fail_detail: `code:${loginRes.code};${loginRes.message ?? ''}`,
                });
                throw new Error(loginRes.message || 'auth login failed');
            }

            setSessionReadyCookie();

            const { user_id, login_source } = loginRes.data;
            useUserInfoStore.getState().updateUserInfo({
                user_id,
                login_source: login_source ?? 0,
            });
            useUserInfoStore.getState().setIsLogin(true);

            reportGuestSessionSuccess({
                user_id,
                login_source: login_source ?? 0,
                tourist_device_id: registerRes.data.device_id,
            });

            await registerTrackerParams({
                user_id,
                install_seq,
                device_uuid,
            });

            syncArmsConfig();
        } catch (err) {
            if (!guestSessionFailReported) {
                reportGuestSessionFailFromError(err);
            }
            throw err;
        }
    })().catch(err => {
        establishSessionPromise = null;
        throw err;
    });

    return establishSessionPromise;
}

/**
 * 进入 H5 后顺序：establishSession → 拉用户资料。
 * 使用模块级 Promise，避免 React Strict Mode 下重复发起冷启动请求。
 */
export function runSessionBootstrap(): Promise<void> {
    if (bootstrapPromise) {
        return bootstrapPromise;
    }
    bootstrapPromise = (async () => {
        await establishSession();
        await hydrateUserProfileFromApi();
    })().catch(err => {
        bootstrapPromise = null;
        throw err;
    });

    return bootstrapPromise;
}

registerSessionBootstrapRunner(establishSession);
